import React from "react";
import { useModeAwareQuery, useModeAwareMutation } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { useQueryClient } from "@tanstack/react-query";
import { mapDisputeApiToUi, mapDisputeDetailApiToUi, mapSummaryToKpis } from "@/lib/mappers/disputes";
import { useDisputeTypesList } from "./useDisputeTypesData";
import { GlobalDataStore } from "@/lib/mock-data/global-data-store";
import type { DisputeUI, DisputesKpisUI } from "@/types/ui/disputes";
import type { TransactionType } from "@/types/transaction";
import type { 
  DisputesApiResponse, 
  DisputeDetailApi,
  DisputesDashboardApi,
  CreateDisputeRequest,
  UpdateDisputeRequest,
  CreateDisputeResponse,
  UpdateDisputeResponse,
  DisputeTypeApi,
} from "@/types/api/disputes";
import type { CustomersApiResponse } from "@/types/api/customers";
import type { UserApi } from "@/types/api/users";

export interface GetDisputesParams extends Record<string, string | number | boolean | undefined> {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  priority?: string;
  disputeType?: string;
  customerId?: number;
  assignedToUserId?: number;
  amountMin?: number;
  amountMax?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

// Helper to map transaction status + dueDate to display status
function getDisputeTransactionStatus(status: string, dueDate?: string): 'outstanding' | 'overdue' | 'completed' {
  const lowerStatus = status.toLowerCase();
  // Credited / paid / resolved → completed
  if (lowerStatus === 'credited' || lowerStatus === 'paid' || lowerStatus === 'completed') {
    return 'completed';
  }
  // Outstanding / disputed / pending → check dueDate for overdue
  if (dueDate && new Date(dueDate) < new Date()) {
    return 'overdue';
  }
  return 'outstanding';
}

// Normalise raw store type string to typed TransactionType
function normaliseTransactionType(type: string): TransactionType {
  const map: Record<string, TransactionType> = {
    'invoice':            'invoice',
    'credit note':        'credit_note',
    'credit_note':        'credit_note',
    'receipt':            'receipt',
    'debit adjustment':   'debit_adjustment',
    'debit_adjustment':   'debit_adjustment',
    'credit adjustment':  'credit_adjustment',
    'credit_adjustment':  'credit_adjustment',
  };
  return map[type?.toLowerCase()] ?? 'invoice';
}

// Helper function to convert MockDispute to DisputeUI
function convertMockDisputeToUI(mockDispute: any): DisputeUI {
  return {
    id: mockDispute.id,
    numericId: mockDispute.numericId,
    customer: mockDispute.customer,
    customerId: parseInt(mockDispute.customerId),
    customerAccountCode: mockDispute.customerAccountCode || mockDispute.id.replace('DSP', 'CUS'), // Use provided or generate
    contactName: mockDispute.contactName || "Unknown Contact",
    contactEmail: mockDispute.contactEmail || "contact@example.com",
    contactPhone: mockDispute.contactPhone || "+44 20 7000 0000",
    type: mockDispute.type,
    amount: mockDispute.amount,
    status: mockDispute.status,
    priority: mockDispute.priority,
    assignedTo: mockDispute.assignedTo,
    createdDate: mockDispute.createdDate instanceof Date 
      ? mockDispute.createdDate.toISOString().split('T')[0] 
      : mockDispute.createdDate,
    dueDate: mockDispute.dueDate instanceof Date 
      ? mockDispute.dueDate.toISOString().split('T')[0] 
      : mockDispute.dueDate,
    workflow: mockDispute.workflow,
    aiScore: mockDispute.aiScore,
    aiRecommendation: mockDispute.aiRecommendation,
    currentLevel: mockDispute.currentLevel,
    totalLevels: mockDispute.totalLevels,
    description: mockDispute.description,
    resolutionNotes: mockDispute.resolutionNotes,
    // Resolution fields
    resolutionOutcome: mockDispute.resolutionOutcome,
    resolutionAmount: mockDispute.resolutionAmount,
    resolutionDate: mockDispute.resolutionDate,
    resolvedBy: mockDispute.resolvedBy,
    resolutionDetails: mockDispute.resolutionDetails,
    // Closed fields
    closedReason: mockDispute.closedReason,
    closedDate: mockDispute.closedDate,
    closedNote: mockDispute.closedNote,
    // Convert transactions from global data store format to UI format
    transactions: mockDispute.transactions?.map((t: any) => ({
      id: t.id,
      referenceNumber: t.referenceNumber,
      date: t.transactionDate,
      dueDate: t.dueDate,
      description: `${t.type} - ${t.referenceNumber}`,
      transactionType: normaliseTransactionType(t.type),
      amount: t.amount,
      currency: t.currency || 'GBP',
      status: getDisputeTransactionStatus(t.status, t.dueDate)
    })) || []
  };
}

// Get mock disputes from global data store
function getMockDisputesFromStore(): DisputeUI[] {
  const store = GlobalDataStore.getInstance();
  const mockDisputes = store.getDisputes();
  const converted = mockDisputes.map(convertMockDisputeToUI);
  return converted;
}

function computeMockDisputesKpis(disputes: DisputeUI[]): DisputesKpisUI {
  const totalDisputes = disputes.length;
  const totalAmount = disputes.reduce((sum, d) => sum + d.amount, 0);
  const newCount = disputes.filter((d) => d.status === "New").length;
  const inProgressCount = disputes.filter((d) => d.status === "In Progress" || d.status === "Under Review").length;
  const resolvedCount = disputes.filter((d) => d.status === "Resolved" || d.status === "Closed").length;
  const highPriorityCount = disputes.filter((d) => d.priority === "High" || d.priority === "Critical").length;
  return { totalDisputes, totalAmount, newCount, inProgressCount, resolvedCount, highPriorityCount };
}

export interface DisputesListResult {
  disputes: DisputeUI[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

function computeMockPagination(
  totalItems: number,
  page: number,
  pageSize: number,
): DisputesListResult['pagination'] {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  return {
    page,
    pageSize,
    totalItems,
    totalPages,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };
}

function filterMockDisputes(
  disputes: DisputeUI[],
  params?: GetDisputesParams,
): DisputeUI[] {
  let filtered = disputes;

  if (params?.customerId !== undefined) {
    filtered = filtered.filter(d => d.customerId === params.customerId);
  }

  if (params?.search) {
    const query = params.search.toLowerCase();
    filtered = filtered.filter(d =>
      d.id.toLowerCase().includes(query) ||
      d.customer.toLowerCase().includes(query) ||
      d.type.toLowerCase().includes(query) ||
      d.description.toLowerCase().includes(query),
    );
  }

  if (params?.status && params.status !== 'all') {
    const statuses = params.status.split(',');
    filtered = filtered.filter(d => statuses.includes(d.status));
  }

  if (params?.priority && params.priority !== 'all') {
    const priorities = params.priority.split(',');
    filtered = filtered.filter(d => priorities.includes(d.priority));
  }

  if (params?.disputeType) {
    const types = params.disputeType.split(',');
    filtered = filtered.filter(d => types.includes(d.type));
  }

  // In demo mode, mock disputes use string assignedTo — skip numeric user ID filtering

  return filtered;
}

function sortMockDisputes(
  disputes: DisputeUI[],
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
): DisputeUI[] {
  const sorted = [...disputes];
  const order = sortOrder === 'asc' ? 1 : -1;

  sorted.sort((a, b) => {
    switch (sortBy) {
      case 'customer':
        return order * a.customer.localeCompare(b.customer);
      case 'type':
        return order * a.type.localeCompare(b.type);
      case 'amount':
        return order * (a.amount - b.amount);
      case 'status':
        return order * a.status.localeCompare(b.status);
      case 'priority':
        return order * a.priority.localeCompare(b.priority);
      case 'dueDate':
        return order * ((a.dueDate || '').localeCompare(b.dueDate || ''));
      case 'assignedTo':
        return order * (a.assignedTo || '').localeCompare(b.assignedTo || '');
      default:
        return order * (new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
    }
  });

  return sorted;
}

export function useDisputesList(params?: GetDisputesParams) {
  const { apiGet } = useApi();
  const queryParams: GetDisputesParams = { page: 1, pageSize: 20, sortBy: 'createdAt', sortOrder: 'desc', ...params };

  const allMockDisputes = getMockDisputesFromStore();
  const filteredMock = filterMockDisputes(allMockDisputes, queryParams);
  const sortedMock = sortMockDisputes(filteredMock, queryParams.sortBy, queryParams.sortOrder);
  const mockPage = queryParams.page ?? 1;
  const mockPageSize = queryParams.pageSize ?? 20;
  const mockPaged = sortedMock.slice((mockPage - 1) * mockPageSize, mockPage * mockPageSize);
  const mockResult: DisputesListResult = {
    disputes: mockPaged,
    pagination: computeMockPagination(sortedMock.length, mockPage, mockPageSize),
  };

  return useModeAwareQuery<DisputesListResult, Error>(
    ["disputes", queryParams],
    async ({ signal }) => {
      const response = await apiGet<DisputesApiResponse>("/api/v1/disputes", queryParams, signal);
      const disputes = response.data.map(mapDisputeApiToUi);
      return {
        disputes,
        pagination: {
          page: response.pagination.page,
          pageSize: response.pagination.pageSize,
          totalItems: response.pagination.totalItems,
          totalPages: response.pagination.totalPages,
          hasNextPage: response.pagination.hasNextPage,
          hasPreviousPage: response.pagination.hasPreviousPage,
        },
      };
    },
    mockResult,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

export function useDisputeById(disputeId: string | undefined) {
  const { apiGet } = useApi();

  // For demo mode, find from mock store
  const allMockDisputes = getMockDisputesFromStore();
  const mockDispute = allMockDisputes.find(d => d.id === disputeId) ?? undefined;

  return useModeAwareQuery<DisputeUI | undefined, Error>(
    ["dispute", disputeId],
    async ({ signal }) => {
      if (!disputeId) return undefined;
      // First resolve the numeric ID via the list endpoint (URL param is disputeNumber e.g. "DSP-001")
      const listResponse = await apiGet<DisputesApiResponse>(
        "/api/v1/disputes",
        { search: disputeId, page: 1, pageSize: 1 },
        signal,
      );
      const match = listResponse.data.find(
        (d) => d.disputeNumber === disputeId,
      );
      if (!match) return undefined;

      // Fetch from the detail endpoint which includes transactions
      const detail = await apiGet<DisputeDetailApi>(
        `/api/v1/disputes/${match.id}`,
        undefined,
        signal,
      );
      return mapDisputeDetailApiToUi(detail);
    },
    mockDispute,
    {
      enabled: !!disputeId,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

export function useDisputesDashboard() {
  const { apiGet } = useApi();
  const mockKpis = computeMockDisputesKpis(getMockDisputesFromStore());

  return useModeAwareQuery<DisputesKpisUI, Error>(
    ["disputes-dashboard"],
    async ({ signal }) => {
      const response = await apiGet<DisputesDashboardApi>("/api/v1/disputes/dashboard", undefined, signal);
      return mapSummaryToKpis(response);
    },
    mockKpis,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

const defaultPagination: DisputesListResult['pagination'] = {
  page: 1,
  pageSize: 20,
  totalItems: 0,
  totalPages: 1,
  hasNextPage: false,
  hasPreviousPage: false,
};

export function useDisputesPageData(params?: GetDisputesParams) {
  const disputesQuery = useDisputesList(params);
  const dashboardQuery = useDisputesDashboard();

  return {
    disputes: disputesQuery.data?.disputes ?? [],
    pagination: disputesQuery.data?.pagination ?? defaultPagination,
    kpis: dashboardQuery.data ?? {
      totalDisputes: 0,
      totalAmount: 0,
      newCount: 0,
      inProgressCount: 0,
      resolvedCount: 0,
      highPriorityCount: 0,
    },
    isLoading: disputesQuery.isLoading || dashboardQuery.isLoading,
    error: disputesQuery.error || dashboardQuery.error,
    refetch: () => {
      disputesQuery.refetch();
      dashboardQuery.refetch();
    },
  };
}

export function useCreateDispute() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CreateDisputeResponse, Error, CreateDisputeRequest>(
    async (data) => {
      return apiPost<CreateDisputeResponse>("/api/v1/disputes", data);
    },
    async () => {
      return {
        id: Date.now(),
        disputeNumber: `DSP-${String(Date.now()).slice(-3)}`,
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["disputes"] });
        queryClient.invalidateQueries({ queryKey: ["disputes-dashboard"] });
      },
    }
  );
}

export function useUpdateDispute(disputeId: number) {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<UpdateDisputeResponse, Error, UpdateDisputeRequest>(
    async (data) => {
      return apiPut<UpdateDisputeResponse>(`/api/v1/disputes/${disputeId}`, data);
    },
    async () => {
      return {
        id: disputeId,
        disputeNumber: `DSP-${String(disputeId).padStart(3, "0")}`,
        message: "Dispute updated successfully",
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["disputes"] });
        queryClient.invalidateQueries({ queryKey: ["disputes-dashboard"] });
      },
    }
  );
}

// Priority/status ID → string label maps (must match backend seed data)
const PRIORITY_MAP: Record<number, string> = { 1: "Low", 2: "Medium", 3: "High", 4: "Critical" };
const STATUS_MAP: Record<number, string> = { 1: "New", 2: "Under Review", 3: "In Progress", 4: "Escalated", 5: "Resolved", 6: "Closed" };

export interface QuickUpdateDisputeRequest {
  /** String dispute ID, e.g. "DSP-001" */
  disputeStringId: string;
  /** Numeric database ID for the dispute */
  numericId?: number;
  priority?: string;
  /** Numeric priority ID from the priorities API - bypasses PRIORITY_MAP lookup */
  priorityId?: number;
  status?: string;
  assignedTo?: string;
  assignedToUserId?: number;
  closedReason?: string;
  closedDate?: string;
  closedNote?: string;
  resolutionNotes?: string;
  resolutionOutcome?: string;
  resolutionAmount?: number | null;
  resolutionDate?: string;
  resolvedBy?: string;
}

/**
 * Hook for quick-action updates that work directly with string IDs and string values.
 * Supports both single and bulk updates (pass an array of disputeStringIds).
 */
export function useQuickUpdateDisputes() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();
  const store = GlobalDataStore.getInstance();

  return useModeAwareMutation<void, Error, QuickUpdateDisputeRequest[]>(
    async (updates) => {
      // Live mode: fire individual PUT requests for each dispute
      await Promise.all(
        updates.map(({ disputeStringId, numericId, priority, priorityId, status, assignedTo, assignedToUserId, closedReason, closedDate, closedNote, resolutionNotes, resolutionOutcome, resolutionAmount }) => {
          const disputeId = numericId ?? parseInt(disputeStringId.replace(/\D/g, ""), 10);
          const body: UpdateDisputeRequest = {};
          if (priorityId) {
            body.priorityId = priorityId;
          } else if (priority) {
            const entry = Object.entries(PRIORITY_MAP).find(([, v]) => v === priority);
            if (entry) body.priorityId = parseInt(entry[0]);
          }
          if (status) {
            const entry = Object.entries(STATUS_MAP).find(([, v]) => v === status);
            if (entry) body.statusId = parseInt(entry[0]);
          }
          if (resolutionNotes) body.resolutionNotes = resolutionNotes;
          if (closedReason !== undefined) body.closedReason = closedReason;
          if (closedDate !== undefined) body.closedDate = closedDate;
          if (closedNote !== undefined) body.closedNote = closedNote;
          if (assignedToUserId) body.assignedToUserId = assignedToUserId;
          if (resolutionOutcome) body.resolutionOutcome = resolutionOutcome;
          if (resolutionAmount !== undefined && resolutionAmount !== null) body.resolutionAmount = resolutionAmount;
          return apiPut<UpdateDisputeResponse>(`/api/v1/disputes/${disputeId}`, body);
        })
      );
    },
    async (updates) => {
      // Mock mode: update GlobalDataStore directly with string values
      updates.forEach(({ disputeStringId, priority, status, assignedTo, closedReason, closedDate, closedNote, resolutionNotes, resolutionOutcome, resolutionAmount, resolutionDate, resolvedBy }) => {
        const patch: Record<string, unknown> = {};
        if (priority !== undefined) patch.priority = priority;
        if (status !== undefined) patch.status = status;
        if (assignedTo !== undefined) patch.assignedTo = assignedTo;
        if (closedReason !== undefined) patch.closedReason = closedReason;
        if (closedDate !== undefined) patch.closedDate = closedDate;
        if (closedNote !== undefined) patch.closedNote = closedNote;
        if (resolutionNotes !== undefined) patch.resolutionNotes = resolutionNotes;
        if (resolutionOutcome !== undefined) patch.resolutionOutcome = resolutionOutcome;
        if (resolutionAmount !== undefined) patch.resolutionAmount = resolutionAmount;
        if (resolutionDate !== undefined) patch.resolutionDate = resolutionDate;
        if (resolvedBy !== undefined) patch.resolvedBy = resolvedBy;
        store.updateDispute(disputeStringId, patch as any);
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["disputes"] });
        queryClient.invalidateQueries({ queryKey: ["disputes-dashboard"] });
      },
    }
  );
}

export interface DisputePriorityOption {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
}

const mockDisputePriorities: DisputePriorityOption[] = [
  { id: 4, code: "Low", name: "Low", sortOrder: 4 },
  { id: 3, code: "Medium", name: "Medium", sortOrder: 3 },
  { id: 2, code: "High", name: "High", sortOrder: 2 },
  { id: 1, code: "Critical", name: "Critical", sortOrder: 1 },
];

export function useDisputePriorities() {
  const { apiGet } = useApi();

  return useModeAwareQuery<DisputePriorityOption[], Error>(
    ["dispute-priorities"],
    async ({ signal }) => {
      return apiGet<DisputePriorityOption[]>("/api/v1/disputes/priorities", undefined, signal);
    },
    mockDisputePriorities,
    { staleTime: 60 * 1000, refetchOnWindowFocus: false }
  );
}

export interface CustomerOption {
  id: number;
  name: string;
}

export interface DisputeTypeOption {
  id: string;
  name: string;
}

export interface UserOption {
  id: number;
  name: string;
}

const mockCustomerOptions: CustomerOption[] = [
  { id: 1, name: "Acme Corporation" },
  { id: 2, name: "TechStart Inc" },
  { id: 3, name: "Global Solutions Ltd" },
  { id: 4, name: "Enterprise Systems Co" },
  { id: 5, name: "Innovate Labs" },
];

const mockUserOptions: UserOption[] = [
  { id: 1, name: "John Smith" },
  { id: 2, name: "Sarah Johnson" },
  { id: 3, name: "Mike Chen" },
  { id: 4, name: "Emily Davis" },
  { id: 5, name: "David Wilson" },
];

export function useCustomerOptions() {
  const { apiGet } = useApi();

  return useModeAwareQuery<CustomerOption[], Error>(
    ["customer-options"],
    async ({ signal }) => {
      const response = await apiGet<CustomersApiResponse>("/api/v1/customers", { pageSize: 1000 }, signal);
      return response.data.map((c) => ({ id: parseInt(c.id), name: c.name }));
    },
    mockCustomerOptions,
    { staleTime: 5 * 60 * 1000 }
  );
}

export function useDisputeTypeOptions() {
  // Use the same data source as the Dispute Types settings page
  const { data: disputeTypes = [], isLoading, error } = useDisputeTypesList();

  // Transform the full dispute types data into simple options format
  // Filter to only active types
  const options = React.useMemo(() => {
    return disputeTypes
      .filter((dt) => dt.isActive)
      .map((dt) => ({ 
        id: dt.id.toString(), 
        name: dt.name 
      }));
  }, [disputeTypes]);

  // Return in the same format as other option hooks
  return {
    data: options,
    isLoading,
    isError: !!error,
    error,
  };
}

export function useUserOptions() {
  const { apiGet } = useApi();

  return useModeAwareQuery<UserOption[], Error>(
    ["user-options"],
    async ({ signal }) => {
      const response = await apiGet<UserApi[]>("/api/v1/users", undefined, signal);
      return response.map((u) => ({ id: u.id, name: `${u.firstName} ${u.lastName}` }));
    },
    mockUserOptions,
    { staleTime: 5 * 60 * 1000 }
  );
}
